/* eslint-disable react/jsx-fragments */
import React, { useState, useEffect, useCallback } from 'react';
import type { FC } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { READ, EMPRESA } from 'src/utils/rbac/tablePermissionsNames';
import ComponentsByRequiredPrivilegesJSX from 'src/utils/rbac/ComponentsByRequiredPrivilegesJSX';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles, CircularProgress, TextField, Box } from '@material-ui/core';
import { listEmpresaInit } from '../redux/actions';
import { RootState } from '../redux/types';
import type { Theme } from '../theme';
import type { Emp } from '../@types/emp';
import { PAPEL2, PAPELDEFAULT } from 'src/utils/rbac/papeis';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { opcoesAmbiente } from 'src/constants';

interface SetEmpProp {
  (emp: any): boolean;
}
interface AdminEmpSelectionProps {
  handleEmp: SetEmpProp;
}

const useStyles = makeStyles((theme: Theme) => ({
  option: {
    fontSize: '0.8rem', // Tamanho da fonte das opções
    '&[data-focus="true"]': {
      backgroundColor: theme.palette.action.hover
    },
    '&[aria-selected="true"]': {
      backgroundColor: theme.palette.action.selected
    }
  },
  input: {
    fontSize: '0.8rem'
  },
  topoNav: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '5px 10px 0px 10px'
  },
}));

// função que retorna a ordem de cada ambiente
const getAmbienteOrder = (value?: string) => {
  const index = opcoesAmbiente.findIndex(opt => opt.value === value);
  return index === -1 ? 999 : index;
};

const AdminEmpSelection: FC<AdminEmpSelectionProps> = ({ handleEmp }: AdminEmpSelectionProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { allEmps, myEmp, myPrivilege } = useSelector(
    (state: RootState) => ({
      isAdmin: state.account.user.isAdmin,
      allEmps: state.empresa.allEmps,
      myEmp: state.account.myEmp,
      myPrivilege: String(state.account.user?.privilegeHash || PAPELDEFAULT)
    }),
    shallowEqual
  );

  const [empValue, setEmpValue] = useState<Emp | undefined>(myEmp);
  const [allEmpData, setAllEmpData] = useState<Emp[]>([]);
  const [open, setOpen] = React.useState(false);
  const loadingEmp = allEmpData.length === 0;

  // Função para filtrar apenas as empresas ativas
  const filterActiveEmps = (emps: any[]): any[] => {
    return emps.filter((emp) => emp.status === 'Ativa');
  };

  const fetchEmps = () => {
    const activeEmps = filterActiveEmps(allEmps);

    const sortedEmps = [...activeEmps].sort((a, b) => {
      const aOrder = getAmbienteOrder(a.classificacaoAmbiente);
      const bOrder = getAmbienteOrder(b.classificacaoAmbiente);

      if (aOrder !== bOrder) return aOrder - bOrder;

      return (a.identificador || 0) - (b.identificador || 0);
    });

    setAllEmpData(sortedEmps);
  };

  useEffect(() => {
    fetchEmps();
  }, [allEmps, myEmp]);

  const getEmps = useCallback(() => {
    if (!allEmps || allEmps.length === 0) {
      dispatch(listEmpresaInit());
    }
  }, []);

  useEffect(() => {
    getEmps();
  }, [getEmps]);

  const handleChange = (newValue: any | null) => {
    // rotas quando o usuario esta visualizando um painel
    const viewPainelRoutes = [
      '/empresas/:empresaId/lojas/:lojaId/paineis/:painelId',
      '/empresas/:empresaId/paineis/:painelId',
    ];
    const isOnViewPainelRoute = viewPainelRoutes.some((route) =>
      matchPath(location.pathname, { path: route, exact: true })
    );
    setEmpValue(newValue);
    // redireciona para tela inicial apenas se usuario estiver 
    // visualizando um painel
    if (isOnViewPainelRoute) {
      history.push('/conta');
    }
  };

  const handleSelectionEmp = (newValue: any): void => {
    handleEmp(newValue);
  };

  const podeAlternarEmpresa = Number(myPrivilege) > Number(PAPEL2);

  return (
    <>
      <ComponentsByRequiredPrivilegesJSX module={EMPRESA} privileges={[READ]}>
        <Box className={classes.topoNav}>
          <Autocomplete
            fullWidth
            id="empresa"
            options={allEmpData}
            loading={loadingEmp}
            loadingText="Aguarde carregando lista de empresas"
            getOptionLabel={(emp) => emp.identificador != null ? emp.identificador + " - " + emp.name : emp.name}
            value={empValue}
            disableClearable={true}
            size="small"
            onChange={(event, newValue) => {
              handleChange(newValue);
              handleSelectionEmp(newValue);
            }}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={podeAlternarEmpresa ? 'Selecionar Empresa' : ''}
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  classes: { input: classes.option },
                  endAdornment: (
                    <React.Fragment>
                      {open && loadingEmp ? ( // Verifica se open é true e loadingEmp é true
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  )
                }}
              />
            )}
            classes={{
              option: classes.option // Aplica os estilos personalizados às opções
            }}
          />
        </Box>
      </ComponentsByRequiredPrivilegesJSX>
    </>
  );
};

export default AdminEmpSelection;
